'use client'
import { forwardRef } from 'react'
import { CheckIcon } from '@heroicons/react/16/solid'
import { cn } from '../../helpers/classnames'

export interface JosefCheckboxProps {
  checked?: boolean
  onCheckedChange?: (checked: boolean) => void
  disabled?: boolean
  label?: string
  description?: string
  error?: string
  className?: string
  buttonClassName?: string
  id?: string
}

export const JosefCheckbox = forwardRef<HTMLButtonElement, JosefCheckboxProps>(
  (
    {
      className,
      buttonClassName,
      label,
      description,
      error,
      id,
      checked = false,
      onCheckedChange,
      disabled = false,
      ...props
    },
    ref
  ) => {
    const checkboxId =
      id ?? `checkbox-${Math.random().toString(36).substr(2, 9)}`

    return (
      <div className={cn('space-y-2', className)}>
        <div className="flex items-start gap-3">
          <button
            type="button"
            role="checkbox"
            aria-checked={checked}
            data-state={checked ? 'checked' : 'unchecked'}
            id={checkboxId}
            ref={ref}
            disabled={disabled}
            onClick={() => onCheckedChange?.(!checked)}
            className={cn(
              'peer size-4 shrink-0 rounded-[4px] border shadow-xs transition-shadow outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 mt-0.5',
              'border-black/20 dark:border-white/20 bg-white dark:bg-black/30',
              'focus-visible:border-black/40 dark:focus-visible:border-white/40 focus-visible:ring-black/20 dark:focus-visible:ring-white/20',
              checked &&
                'bg-black dark:bg-white border-black dark:border-white',
              error &&
                'border-red-500 dark:border-red-400 focus-visible:ring-red-500/20 dark:focus-visible:ring-red-400/20',
              buttonClassName
            )}
            {...props}
          >
            {checked && (
              <div className="grid place-content-center text-current transition-none">
                <CheckIcon className="size-3.5 text-white dark:text-black" />
              </div>
            )}
          </button>
          {(label || description) && (
            <div className="flex-1">
              {label && (
                <div
                  className="block text-sm font-medium text-black dark:text-white cursor-pointer"
                  onClick={() => onCheckedChange?.(!checked)}
                >
                  {label}
                </div>
              )}
              {description && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {description}
                </p>
              )}
            </div>
          )}
        </div>
        {error && (
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        )}
      </div>
    )
  }
)

JosefCheckbox.displayName = 'JosefCheckbox'
